import React, { useState } from "react";
import { Activity, ArrowRight, Dna, Grid3X3, Microscope } from "lucide-react";
import Link from "../audience/ScientificLink";
import {
  AntibodyBindingVisual,
  CellAssayVisual,
  IpscTrajectoryVisual,
  SirnaScreenVisual,
} from "./AssayProgramVisuals";
import "./AssayProgramsCompact.css";

const programs = [
  {
    id: "cell-based-assay-automation",
    title: "Cell-based assays",
    description:
      "We automate plate setup, dosing, timed incubations, imaging, and analysis while preserving the handling windows your cells need.",
    icon: Microscope,
    Visual: CellAssayVisual,
  },
  {
    id: "ipsc-differentiation",
    title: "iPSC differentiation",
    description:
      "We coordinate media changes, plate moves, microscopy checkpoints, and QC decisions across a multi-day differentiation.",
    icon: Activity,
    Visual: IpscTrajectoryVisual,
  },
  {
    id: "sirna-screening",
    title: "siRNA screens",
    description:
      "We handle library plates, controls, plate layouts, replicate strategy, plate QC, and the handoff into hit review.",
    icon: Grid3X3,
    Visual: SirnaScreenVisual,
  },
  {
    id: "antibody-screening",
    title: "Antibody screens",
    description:
      "We automate dilution series, wash steps, binding readouts, and candidate review against scientist-approved criteria.",
    icon: Dna,
    Visual: AntibodyBindingVisual,
  },
];

const AssayPrograms = ({ compact = false }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeProgram = programs[activeIndex];
  const ActiveVisual = activeProgram.Visual;

  const selectProgram = (index) => {
    setActiveIndex(index);
    if (compact || typeof window === "undefined" || window.innerWidth >= 1024) return;

    window.requestAnimationFrame(() => {
      const panel = document.getElementById("assay-program-panel");
      if (!panel) return;
      const reducedMotion = window.matchMedia?.(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      window.scrollTo({
        top: Math.max(
          0,
          window.scrollY + panel.getBoundingClientRect().top - 88
        ),
        behavior: reducedMotion ? "auto" : "smooth",
      });
    });
  };

  const onTabKeyDown = (event) => {
    if (
      ![
        "ArrowRight",
        "ArrowLeft",
        "ArrowDown",
        "ArrowUp",
        "Home",
        "End",
      ].includes(event.key)
    ) {
      return;
    }
    event.preventDefault();
    let nextIndex = activeIndex;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (activeIndex + 1) % programs.length;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (activeIndex - 1 + programs.length) % programs.length;
    }
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = programs.length - 1;
    setActiveIndex(nextIndex);
    document.getElementById(`assay-tab-${programs[nextIndex].id}`)?.focus();
  };

  if (compact) {
    return (
      <section
        id="assay-programs"
        data-primary-nav="solutions"
        className="assay-programs-compact"
        aria-labelledby="assay-programs-title"
      >
        <header className="assay-compact-header">
          <h2 id="assay-programs-title">Assays we’ve helped automate.</h2>
          <p>Illustrative views of assay types we’ve worked on.</p>
        </header>

        <div className="assay-compact-shell">
          <div
            role="tablist"
            aria-label="Assay examples"
            onKeyDown={onTabKeyDown}
            className="assay-compact-tabs"
          >
            {programs.map((program, index) => {
              const Icon = program.icon;
              const active = index === activeIndex;
              return (
                <button
                  key={program.id}
                  id={`assay-tab-${program.id}`}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls="assay-program-panel"
                  tabIndex={active ? 0 : -1}
                  onClick={() => selectProgram(index)}
                  className="assay-compact-tab"
                >
                  <Icon aria-hidden="true" />
                  <span>{program.title}</span>
                </button>
              );
            })}
          </div>

          <div
            id="assay-program-panel"
            role="tabpanel"
            aria-labelledby={`assay-tab-${activeProgram.id}`}
            tabIndex={0}
            className="assay-compact-panel"
          >
            <div className="assay-compact-visual">
              <ActiveVisual key={activeProgram.id} />
            </div>
            <div className="assay-compact-caption">
              <p>{activeProgram.description}</p>
              <Link
                to={`/contact?solution=${activeProgram.id}&source=assay-programs`}
              >
                Talk through this assay
                <ArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="assay-programs"
      data-primary-nav="solutions"
      className="section-anchor border-b border-slate-200 bg-[#f3f7fb] px-6 py-24 text-slate-950 dark:border-white/[0.08] dark:bg-[#07090d] dark:text-white sm:py-32"
      aria-labelledby="assay-programs-title"
    >
      <div className="wide-shell-xl mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-[0.37fr_0.63fr] lg:gap-20">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">
            Assays we&apos;ve worked on
          </p>
          <div>
            <h2
              id="assay-programs-title"
              className="max-w-4xl font-serif text-5xl font-normal leading-[0.98] tracking-[-0.035em] sm:text-6xl lg:text-8xl"
            >
              A few assays we&apos;ve <span className="text-primary">helped automate.</span>
            </h2>
            <p className="mt-7 max-w-3xl text-lg leading-8 text-slate-600 dark:text-white/70">
              Cell-based assays, iPSC differentiation workflows, siRNA screens,
              and antibody screens are a few examples. We&apos;ve also worked on
              biochemical enzyme assays, viability and cytotoxicity screens,
              reporter-gene assays, and high-content imaging workflows.
            </p>
          </div>
        </div>

        <div className="mt-16 grid overflow-hidden rounded-[1.75rem] border border-slate-300 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.1)] dark:border-white/[0.12] dark:bg-[#0a0d13] dark:shadow-none lg:grid-cols-[0.38fr_0.62fr]">
          <div
            role="tablist"
            aria-label="Assay examples"
            onKeyDown={onTabKeyDown}
            className="grid grid-cols-2 border-b border-slate-300 dark:border-white/[0.1] lg:block lg:border-b-0 lg:border-r"
          >
            {programs.map((program, index) => {
              const Icon = program.icon;
              const active = index === activeIndex;
              return (
                <button
                  key={program.id}
                  id={`assay-tab-${program.id}`}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls="assay-program-panel"
                  tabIndex={active ? 0 : -1}
                  onClick={() => selectProgram(index)}
                  className={`group flex min-h-[88px] w-full cursor-pointer items-center gap-3 border-slate-200 px-4 py-5 text-left outline-none transition-colors focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary dark:border-white/[0.08] sm:gap-4 sm:px-6 lg:min-h-[104px] lg:px-7 lg:py-6 ${
                    index < 2 ? "border-b" : ""
                  } ${index % 2 === 0 ? "border-r" : ""} ${
                    index < programs.length - 1
                      ? "lg:border-b"
                      : "lg:border-b-0"
                  } lg:border-r-0 ${
                    active
                      ? "bg-primary text-white"
                      : "bg-white hover:bg-slate-50 dark:bg-[#0a0d13] dark:hover:bg-white/[0.035]"
                  }`}
                >
                  <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${active ? "text-white" : "text-primary"}`} aria-hidden="true" />
                  <span className={`block text-base font-semibold ${active ? "text-white" : "text-slate-950 dark:text-white"}`}>
                    {program.title}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            id="assay-program-panel"
            role="tabpanel"
            aria-labelledby={`assay-tab-${activeProgram.id}`}
            className="relative min-h-[520px] overflow-hidden p-4 sm:min-h-[580px] sm:p-8 lg:p-10"
          >
            <div className="assay-program-halo pointer-events-none absolute inset-0" aria-hidden="true" />
            <div
              key={activeProgram.id}
              className="relative flex h-full flex-col"
            >
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-[#f7faff] p-2 dark:border-white/[0.09] dark:bg-[#05070a] sm:p-5">
                <ActiveVisual />
              </div>
              <div className="mt-8 grid gap-6 md:grid-cols-[1fr_0.8fr] md:items-end">
                <div>
                  <h3 className="font-serif text-4xl font-normal tracking-[-0.025em] sm:text-5xl">
                    {activeProgram.title}
                  </h3>
                  <p className="mt-4 max-w-xl text-base leading-7 text-slate-600 dark:text-white/70">
                    {activeProgram.description}
                  </p>
                </div>
                <div className="md:text-right">
                  <Link
                    to={`/contact?solution=${activeProgram.id}&source=assay-programs`}
                    className="group inline-flex min-h-11 items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-primary-700 outline-none transition-colors hover:text-slate-950 focus-visible:ring-2 focus-visible:ring-primary dark:text-primary-400 dark:hover:text-white"
                  >
                    Talk through this assay
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 grid gap-8 border-t border-slate-300 pt-10 dark:border-white/[0.12] lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div>
            <h3 className="max-w-4xl font-serif text-4xl font-normal leading-[1.02] tracking-[-0.03em] sm:text-5xl">
              Bring us an SOP. If the assay still needs work, bring that too.
            </h3>
            <p className="mt-5 max-w-3xl text-base leading-7 text-slate-600 dark:text-white/70">
              If the assay already works, we can onboard it without losing its
              controls, timing, limits, or review points. If it still needs
              development, we scope that as a separate off-site R&amp;D project,
              then transfer the mature SOP into automation. We do not use your
              site as our assay-development lab.
            </p>
          </div>
          <div className="flex flex-col items-start gap-3 sm:flex-row lg:flex-col lg:items-stretch">
            <Link
              to="/contact?solution=assay-automation&source=assay-examples"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-3 text-sm font-semibold text-white outline-none transition-colors hover:bg-primary-700 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#07090d]"
            >
              Onboard an SOP
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              to="/contact?solution=bespoke-assay-development&source=assay-examples"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-800 outline-none transition-colors hover:border-primary hover:text-primary-700 focus-visible:ring-2 focus-visible:ring-primary dark:border-white/[0.16] dark:text-white dark:hover:border-primary-400 dark:hover:text-primary-400"
            >
              Design an assay
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AssayPrograms;
